"use strict";

import Swiper from 'swiper';
import { FreeMode, Navigation } from 'swiper/modules';

const SELECTOR = '[data-mega-columns-carousel]';
const DESKTOP_QUERY = '(min-width: 992px)';
const NAV_SELECTOR = '.mega-menu-tertiary-carousel-prev, .mega-menu-tertiary-carousel-next';

let desktop = null;
let mutationObserver = null;

function getDesktop() {
	if (!desktop) {
		desktop = window.matchMedia(DESKTOP_QUERY);
	}

	return desktop;
}

function isCarouselVisible(el) {
	const panel = el.closest('[data-mega-group-panel]');
	const mega = el.closest('.mega-menu');

	return Boolean(
		panel
		&& mega
		&& !panel.hasAttribute('hidden')
		&& !mega.hasAttribute('hidden')
	);
}

const EDGE_PX = 2;
const preferPrevByEl = new WeakMap();

function getEdgeState(swiper) {
	if (!swiper || swiper.destroyed) {
		return { locked: true, atStart: true, atEnd: true };
	}

	const min = swiper.minTranslate();
	const max = swiper.maxTranslate();
	const current = swiper.getTranslate();
	const locked = Boolean(swiper.isLocked) || max >= min - EDGE_PX;

	return {
		locked,
		// Soglia stretta: solo a inizio/fine corsa reali (non sugli snap intermedi).
		atStart: locked || current >= min - EDGE_PX,
		atEnd: locked || current <= max + EDGE_PX,
	};
}

function syncNavVisibility(swiper) {
	const el = swiper?.el;

	if (!el) {
		return;
	}

	const prev = el.querySelector('.mega-menu-tertiary-carousel-prev');
	const next = el.querySelector('.mega-menu-tertiary-carousel-next');
	const { locked, atStart, atEnd } = getEdgeState(swiper);

	// Sticky: a fine corsa resta la freccia sinistra finché non si torna all'inizio.
	let preferPrev = Boolean(preferPrevByEl.get(el));

	if (locked) {
		preferPrev = false;
	} else if (atEnd) {
		preferPrev = true;
	} else if (atStart) {
		preferPrev = false;
	}

	preferPrevByEl.set(el, preferPrev);

	const showPrev = !locked && preferPrev;
	const showNext = !locked && !preferPrev;

	if (prev) {
		prev.classList.toggle('is-visible', showPrev);
		prev.setAttribute('aria-hidden', showPrev ? 'false' : 'true');
		prev.tabIndex = showPrev ? 0 : -1;
	}

	if (next) {
		next.classList.toggle('is-visible', showNext);
		next.setAttribute('aria-hidden', showNext ? 'false' : 'true');
		next.tabIndex = showNext ? 0 : -1;
	}
}

function bindNavFocusGuard(el) {
	el.querySelectorAll(NAV_SELECTOR).forEach(function (button) {
		if (button.dataset.megaNavBound === '1') {
			return;
		}

		button.dataset.megaNavBound = '1';

		// Evita che il click sposti il focus fuori dal trigger del mega menu.
		button.addEventListener('mousedown', function (event) {
			event.preventDefault();
		});

		button.addEventListener('click', function (event) {
			event.preventDefault();
			event.stopPropagation();
		});
	});
}

function createCarousel(el) {
	const prevEl = el.querySelector('.mega-menu-tertiary-carousel-prev');
	const nextEl = el.querySelector('.mega-menu-tertiary-carousel-next');

	bindNavFocusGuard(el);

	const swiper = new Swiper(el, {
		modules: [FreeMode, Navigation],
		slidesPerView: 'auto',
		spaceBetween: 0,
		grabCursor: true,
		observer: true,
		observeParents: true,
		watchOverflow: true,
		freeMode: {
			enabled: true,
			momentumRatio: 0.85,
			momentumVelocityRatio: 0.85,
		},
		navigation: {
			prevEl: prevEl || undefined,
			nextEl: nextEl || undefined,
		},
		on: {
			init: syncNavVisibility,
			slideChange: syncNavVisibility,
			reachBeginning: syncNavVisibility,
			reachEnd: syncNavVisibility,
			fromEdge: syncNavVisibility,
			progress: syncNavVisibility,
			setTranslate: syncNavVisibility,
			touchEnd: syncNavVisibility,
			transitionEnd: syncNavVisibility,
			resize: syncNavVisibility,
			update: syncNavVisibility,
			lock: syncNavVisibility,
			unlock: syncNavVisibility,
		},
	});

	syncNavVisibility(swiper);

	return swiper;
}

function destroyCarousel(el) {
	if (el.swiper) {
		el.swiper.destroy(true, true);
	}

	preferPrevByEl.delete(el);

	el.querySelectorAll(NAV_SELECTOR).forEach(function (button) {
		button.classList.remove('is-visible');
		button.setAttribute('aria-hidden', 'true');
		button.tabIndex = -1;
	});

	const wrapper = el.querySelector('.swiper-wrapper');

	if (wrapper) {
		wrapper.removeAttribute('style');
	}

	el.querySelectorAll('.swiper-slide').forEach(function (slide) {
		slide.removeAttribute('style');
	});
}

function syncCarousels() {
	const isDesktop = getDesktop().matches;

	document.querySelectorAll(SELECTOR).forEach(function (el) {
		if (!isDesktop) {
			destroyCarousel(el);
			return;
		}

		if (!isCarouselVisible(el)) {
			return;
		}

		if (el.swiper) {
			el.swiper.update();
			syncNavVisibility(el.swiper);
			return;
		}

		createCarousel(el);
	});
}

function observePanels() {
	if (mutationObserver || typeof MutationObserver === 'undefined') {
		return;
	}

	mutationObserver = new MutationObserver(syncCarousels);

	document.querySelectorAll('.mega-menu, [data-mega-group-panel]').forEach(function (node) {
		mutationObserver.observe(node, {
			attributes: true,
			attributeFilter: ['hidden', 'class'],
		});
	});
}

function initMegaMenuColumnsCarousel() {
	observePanels();
	syncCarousels();

	const media = getDesktop();

	if (typeof media.addEventListener === 'function') {
		media.addEventListener('change', syncCarousels);
	} else if (typeof media.addListener === 'function') {
		media.addListener(syncCarousels);
	}
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initMegaMenuColumnsCarousel, { once: true });
} else {
	initMegaMenuColumnsCarousel();
}
